import request from '@/utils/request';
import { getCommunityId } from '@/utils/vc.js';

/**
 * 批量删除合同接口
 *
 * 功能：调用后端批量删除合同接口，支持同时删除多个合同记录
 *
 * @param ids 要删除的合同ID数组，数组元素必须为有效的整数
 * @returns Promise<boolean> 删除操作结果，成功返回true，失败抛出错误
 *
 * 接口：DELETE /contract/delete/batch
 * 时间：2026-03-24
 */
export function batchDeleteContract(ids) {
	return request({
		url: '/api/contract/delete/batch',
		method: 'delete',
		data: ids,
	});
}

/**
 * 删除合同接口
 *
 * 功能：调用后端删除单个合同接口，根据合同ID删除指定合同记录
 *
 * @param id 要删除的合同ID，必须为有效的整数
 * @returns Promise<boolean> 删除操作结果，成功返回true，失败抛出错误
 *
 * 接口：DELETE /contract/delete/{id}
 * 时间：2026-03-24
 */
export function deleteContract(id) {
	return request({
		url: `/api/contract/delete/${id}`,
		method: 'delete',
	});
}

/**
 * 获取合同详情
 *
 * 功能：根据合同ID获取合同的详细信息
 *
 * @param id 合同ID，必须为有效的整数
 * @returns Promise<合同详情对象> 包含合同完整信息
 *
 * 接口：GET /contract/detail/{id}
 * 时间：2026-03-24
 */
export function getContractDetail(id) {
	return request({
		url: `/api/contract/detail/${id}`,
		method: 'get',
	});
}

/**
 * 根据ID获取资源详情接口
 *
 * 功能：根据资源ID查询资源的详细信息
 *
 * @param id 资源ID，必填参数
 * @returns Promise<any> 返回资源详情数据
 *
 * 接口：GET /api/resource/detail/{id}
 * 时间：2026-03-24
 */
export function getResourceDetail(id) {
	return request({
		url: `/api/resource/get/${id}`,
		method: 'get',
	});
}

/**
 * 根据ID查询合同接口
 *
 * 功能：根据合同ID获取合同的详细信息
 *
 * @param id 合同ID，必须为有效的整数
 * @returns Promise<AoContract> 返回合同详情对象
 *
 * 接口：GET /contract/get/{id}
 * 时间：2026-03-24
 */
export function getContractById(id) {
	return request({
		url: `/api/contract/get/${id}`,
		method: 'get',
	});
}

/**
 * 意向书转合同接口
 *
 * 功能：将意向书转换为正式合同，提交合同相关信息
 *
 * @param contractData 合同数据对象，包含资产ID、类型、合同金额、名称、期限等信息
 * @returns Promise<any> 返回接口响应结果
 *
 * 接口：POST /contract/intention-to-contract
 * 时间：2026-03-24
 */
export function intentionToContract(contractData) {
	return request({
		url: '/api/contract/intention-to-contract',
		method: 'post',
		data: contractData,
	});
}

/**
 * 获取合同列表接口
 *
 * 功能：分页或条件查询合同列表数据
 *
 * @param params 查询参数对象，可包含分页、筛选等条件
 * @returns Promise<any> 返回合同列表数据
 *
 * 接口：GET /contract/list
 * 请求数据类型：application/x-www-form-urlencoded
 * 时间：2026-03-24
 */
export function getContractList(params) {
	return request({
		url: '/api/contract/list',
		method: 'get',
		params: params,
	});
}

/**
 * 分页查询合同接口
 *
 * 功能：根据条件分页查询合同列表
 *
 * @param queryParams 查询参数对象，包含社区ID、合同名称、编号、类型、页码、页大小、支付状态等
 * @returns Promise<any> 返回分页合同列表数据
 *
 * 接口：POST /contract/page
 * 时间：2026-03-24
 */
export function pageQueryContracts(queryParams) {
	return request({
		url: '/api/contract/page',
		method: 'post',
		data: {
			communityId: getCommunityId(),
			...queryParams,
		},
	});
}

/**
 * 保存合同接口
 *
 * 功能：创建或更新合同信息
 *
 * @param contractData 合同数据对象，包含资产ID、类型、合同金额、名称、期限、签约方信息等
 * @returns Promise<any> 返回接口响应结果
 *
 * 接口：POST /contract/save
 * 时间：2026-03-24
 */
export function saveContract(contractData) {
	return request({
		url: '/api/contract/save',
		method: 'post',
		data: contractData,
	});
}

/**
 * 获取甲方列表
 * 接口路径：GET /api/contract-partya/list
 * 请求类型：application/x-www-form-urlencoded
 * 响应数据类型：Result<甲方列表>
 * 功能：api/contract-partya/list
 */
export function getContractPartyaList() {
	return request({
		url: `/api/contract-partya/list`, // 路径参数传递交易流水ID
		method: 'get',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
	});
}

/**
 * 更新合同接口
 *
 * 功能：根据合同ID更新合同的详细信息
 *
 * @param contractData 合同数据对象，必须包含id字段以及需要更新的其他字段
 * @returns Promise<any> 返回接口响应结果
 *
 * 接口：PUT /contract/update
 * 时间：2026-03-24
 */
export function updateContract(contractData) {
	return request({
		url: '/api/contract/update',
		method: 'put',
		data: contractData,
	});
}

/**
 * 分页获取资产接口
 *
 * 功能：分页查询资产列表，支持多条件筛选
 *
 * @param params 查询参数对象，包含筛选条件和分页信息
 * @returns Promise<any> 返回资产分页数据
 *
 * 接口：GET /api/asset/page
 * 时间：2026-03-24
 */
export function getAssetPage(params) {
	return request({
		url: '/api/asset/page',
		method: 'get',
		params,
	});
}

/**
 * 保存资产接口
 *
 * 功能：保存资产信息，支持新增或更新资产数据
 *
 * @param data 资产信息对象，符合 AoAsset 结构
 * @returns Promise<any> 返回保存结果
 *
 * 接口：POST /api/asset/save
 * 时间：2026-03-24
 */
export function saveAsset(data) {
	return request({
		url: '/api/asset/save',
		method: 'post',
		data,
	});
}

/**
 * 资产统计接口
 *
 * 功能：获取资产统计信息，用于展示资产概览数据
 *
 * @param params 查询参数对象（当前无参数，预留扩展）
 * @returns Promise<any> 返回资产统计数据
 *
 * 接口：GET /api/asset/statistics
 * 时间：2026-03-24
 */
export function getAssetStatistics(params) {
	return request({
		url: '/api/asset/statistics',
		method: 'get',
		params,
	});
}

/**
 * 根据ID获取资产接口
 *
 * 功能：根据资产ID查询单个资产的详细信息，包含资产基本信息及变更记录
 *
 * @param id 资产ID，必填参数
 * @returns Promise<any> 返回资产详情数据
 *
 * 接口：GET /api/asset/get/{id}
 * 时间：2026-03-24
 */
export function getAssetById(id) {
	return request({
		url: `/api/asset/get/${id}`,
		method: 'get',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
	});
}

/**
 * 删除资源接口
 *
 * 功能：根据资源ID删除指定资源
 *
 * @param id 资源ID，必填参数
 * @returns Promise<any> 返回删除结果
 *
 * 接口：DELETE /api/resource/delete/{id}
 * 时间：2026-03-24
 */
export function deleteResource(id) {
	return request({
		url: `/api/resource/delete/${id}`,
		method: 'delete',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
	});
}

/**
 * 获取资源统计信息接口
 *
 * 功能：获取资源的统计信息，如总数、各状态分布等
 *
 * @param params 查询参数对象（当前无参数，预留扩展）
 * @returns Promise<any> 返回资源统计信息
 *
 * 接口：GET /api/resource/statistics
 * 时间：2026-03-24
 */
export function getResourceStatistics(params) {
	return request({
		url: '/api/resource/statistics',
		method: 'get',
		params: {
			communityId: getCommunityId(),
			...params,
		},
	});
}

/**
 * 获取资源分页列表接口
 *
 * 功能：分页获取资源列表，支持多条件筛选
 *
 * @param data 查询参数对象，包含筛选条件和分页信息
 * @returns Promise<any> 返回资源分页数据
 *
 * 接口：POST /api/resource/page
 * 时间：2026-03-24
 */
export function getResourcePage(data) {
	return request({
		url: '/api/resource/page',
		method: 'post',
		data: {
			communityId: getCommunityId(),
			...data,
		},
	});
}

/**
 * 新增资源接口
 *
 * 功能：新增资源信息，支持创建新的资源记录
 *
 * @param data 资源创建信息对象，符合资源创建VO结构
 * @returns Promise<any> 返回新增结果
 *
 * 接口：POST /api/resource/save
 * 时间：2026-03-24
 */
export function addResource(data) {
	return request({
		url: '/api/resource/save',
		method: 'post',
		data,
	});
}

/**
 * 更新资源接口（最终版）
 *
 * 功能：更新资源信息，支持修改资源的各项属性
 *
 * @param data 资源更新信息对象，符合资源更新VO结构
 * @returns Promise<any> 返回更新结果
 *
 * 接口：PUT /api/resource/update
 * 时间：2026-03-24
 */
export function updateResource(data) {
	return request({
		url: '/api/resource/update',
		method: 'put',
		data,
	});
}

/**
 * 账单缴费接口
 *
 * 功能：根据账单ID进行缴费操作，支持设置实缴金额、缴费日期、备注及凭证URL
 *
 * @param {Object} params 请求参数对象，包含以下可选字段：
 *   - amountPaid {number} 实缴金额，示例值(5000)
 *   - id {number} 账单ID，示例值(1)
 *   - payDate {string} 缴费日期，示例值("2024-01-15")
 *   - remark {string} 备注
 *   - url {string} 缴费凭证URL
 * @returns {Promise<any>} 返回缴费操作结果数据
 *
 * 接口：PUT /api/contract/bill/pay
 * 时间：2026-03-24
 */
export function billPay(params) {
	return request({
		url: '/api/contract/bill/pay',
		method: 'put',
		data: params,
	});
}

// 租赁合同统计数据
export function getStatistics(params) {
	return request({
		url: '/api/resource/statistics',
		method: 'post',
		data: {
			communityId: getCommunityId(),
			...params,
		},
	});
}

//分页获取租控台账列表
export function getRentalLedger(params) {
	return request({
		url: '/api/rental-ledger/page',
		method: 'post',
		data: params,
	});
}

/**
 * 删除交易流水
 * 接口路径：DELETE /api/transaction-flow/delete/{id}
 * 请求类型：application/x-www-form-urlencoded
 * 响应数据类型：Result<boolean>
 * 功能：根据交易流水ID删除对应的交易流水记录
 */
export function deleteTransactionFlow(id) {
	return request({
		url: `/api/transaction-flow/delete/${id}`, // 路径参数传递交易流水ID
		method: 'delete',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
	});
}

/**
 * 获取交易流水详情
 * 接口路径：GET /api/transaction-flow/get/{id}
 * 请求类型：application/x-www-form-urlencoded
 * 响应数据类型：Result<交易流水实体类>
 * 功能：根据交易流水ID查询对应的交易流水详细信息
 */
export function getTransactionFlowDetail(id) {
	return request({
		url: `/api/transaction-flow/get/${id}`, // 路径参数传递交易流水ID
		method: 'get',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
	});
}

/**
 * 分页获取交易流水列表
 * 接口路径：POST /api/transaction-flow/page
 * 请求类型：application/json
 * 响应数据类型：分页数据（含交易流水列表及分页信息）
 * 功能：根据查询条件分页查询交易流水列表
 * @param {Object} pageReqVO - 交易流水分页查询VO（包含accountFlowNo、businessFlowNo、customerName、page、size等参数）
 */
export function pageTransactionFlow(pageReqVO) {
	return request({
		url: '/api/transaction-flow/page',
		method: 'post',
		data: pageReqVO, // 请求体传递分页查询参数
	});
}

/**
 * 新增交易流水
 * 接口路径：POST /api/transaction-flow/save
 * 请求类型：application/json
 * 响应数据类型：Result<boolean>（或交易流水创建结果）
 * 功能：新增一条交易流水记录
 * @param {Object} createVO - 交易流水创建VO（包含accountFlowNo、billingPeriod、businessFlowNo、customerName、expenseSubject、fee等参数）
 */
export function saveTransactionFlow(createVO) {
	return request({
		url: '/api/transaction-flow/save',
		method: 'post',
		data: createVO, // 请求体传递新增数据
	});
}

/**
 * 更新交易流水
 * 接口路径：PUT /api/transaction-flow/update
 * 请求类型：application/json
 * 响应数据类型：Result<boolean>（或交易流水更新结果）
 * 功能：根据ID更新交易流水记录
 * @param {Object} updateVO - 交易流水更新VO（包含accountFlowNo、billingPeriod、businessFlowNo、id、fee等参数）
 */
export function updateTransactionFlow(updateVO) {
	return request({
		url: '/api/transaction-flow/update',
		method: 'put',
		data: updateVO, // 请求体传递更新数据
	});
}

/**
 * 合同解约
 * 功能：根据ID更新交易流水记录
 * @param {Object} params - 表单数据
 */
export function breakContact(params) {
	return request({
		url: '/api/contract/termination/apply',
		method: 'post',
		data: params, // 请求体传递更新数据
	});
}

/**
 * 发起合同电子签署（对接 e签宝 在线签署）
 * POST /api/contract/sign/initiate
 * @param {Object} params - { contractId: number }
 * @returns Promise<{ code, data: { signFlowId, signStatus, resultUrl }, message }>
 */
export function initiateContractSign(params) {
	return request({
		url: '/api/contract/sign/initiate',
		method: 'post',
		data: params,
	});
}

/**
 * 查询楼栋房间信息（按单元和楼层划分）
 * 功能：通过资产类别、资产ID、社区ID、楼层ID、资源类型等条件，查询楼栋下的房间信息（含房屋及保障房）
 * @param {Object} params - 请求参数对象
 * @return {Promise} 返回请求的Promise对象，包含响应数据
 */
export function queryBuildingRoomChart(params) {
	return request({
		url: '/api/rental-chart/buildingRoomChart',
		method: 'post',
		data: {
			communityId: getCommunityId(),
			...params,
		},
	});
}

/**
 * 查询资源及资产信息（车位、广告、铺位）
 * 功能：通过资产类别、资产ID、社区ID、楼层ID、资源类型等条件，查询对应资源及资产信息（车位、广告、铺位等）
 * @param {Object} params - 请求参数对象
 * @return {Promise} 返回请求的Promise对象，包含响应数据
 */
export function queryResourceWithAsset(params) {
	return request({
		url: '/api/rental-chart/listWithAsset',
		method: 'post',
		data: {
			communityId: getCommunityId(),
			...params,
		},
	});
}

/**
 * 统计各租赁状态的房间数量
 * 功能：统计房屋在“已出租、空闲可租、已预定、装修中”等租赁状态下的房间数量
 * @param {Object} params - 请求参数对象（若有参数，需补充；截图中请求参数为空，可留空对象）
 * @return {Promise} 返回请求的Promise对象，包含响应数据（需注意接口实际的请求方法，截图调试GET报错，建议确认后端支持的方法）
 */
export function statisticRoomStatus(data) {
	return request({
		url: '/api/rental-chart/roomStatusStatistics',
		method: 'post',
		data: {
			communityId: getCommunityId(),
			...data,
		},
	});
}

/**
 * 上传文件
 *
 * @param {Object} params - 请求参数对象
 * @return {Promise} 返回请求的Promise对象
 */
export function upload(params) {
	return request({
		url: '/api/file/upload',
		method: 'post',
		data: params,
	});
}

/**
 * 平账
 *
 * @param {Object} params - 请求参数对象
 * @return {Promise} 返回请求的Promise对象
 */
export function reconcile(params) {
	return request({
		url: '/api/transaction-flow/reconcile',
		method: 'post',
		data: params,
	});
}

/**
 * 租控图，广告位，铺位
 *
 * @param {Object} params - 请求参数对象
 * @return {Promise} 返回请求的Promise对象
 */
export function listWithAsset(params) {
	return request({
		url: '/api/rental-chart/listWithAsset',
		method: 'post',
		data: {
			communityId: getCommunityId(),
			...params,
		},
	});
}

/**
 * 租控图
 *
 * @param {Object} params - 请求参数对象
 * @return {Promise} 返回请求的Promise对象
 */
export function parkingSpaceStatistics(params) {
	return request({
		url: '/api/rental-chart/parkingSpaceStatistics',
		method: 'post',
		data: {
			communityId: getCommunityId(),
			...params,
		},
	});
}

/**
 * 租控图，查询停车场
 *
 * @param {Object} params - 请求参数对象
 * @return {Promise} 返回请求的Promise对象
 */
export function getParkingAreaList(communityId) {
	return request({
		url: '/api/parking-area/list-by-community?communityId=' + communityId,
		method: 'get'
	});
}


/**
 * 租控图，车位
 *
 * @param {Object} params - 请求参数对象
 * @return {Promise} 返回请求的Promise对象
 */
export function listWithParkingSpace(params) {
	return request({
		url: '/api/rental-chart/listWithParkingSpace',
		method: 'post',
		data: params,
	});
}


/**
 * 获取租控台账详情
 * @param id 合同ID，必须为有效的整数
 * @returns Promise<合同详情对象> 包含合同完整信息
 *
 * 接口：GET /contract/detail/{id}
 * 时间：2026-03-24
 */
export function getrentalDetail(resourceId) {
	return request({
		url: `/api/rental-ledger/detail/${resourceId}`,
		method: 'get',
	});
}